import * as React from "react";
import type {
    FetchAction,
    Query,
    Template,
    TemplateDashboard,
} from "src/components/template/types";
// import getMockData from "../mock-data/work-fundamentals";
import type { TemplateDataSettings } from "src/hooks/use-template-data-api/types";
import getTemplateMockData from "src/hooks/use-template-data-api/template-mocks";
import useTemplateApi from "./use-template-api";

const useTemplateDataApi = ({
    template,
    defaultDateRange,
    repos,
    groups,
    teamId,
    variables,
    isSample,
    executiveArgs,
}: {
    template: Template;
    defaultDateRange: TemplateDashboard["defaultDateRange"];
    repos?: string[];
    groups?: string[];
    teamId?: string;
    variables: { [name: string]: string };
    isSample?: boolean;
    executiveArgs?: {
        selectedTeam: string;
        aggregationPeriod: string;
        period: number;
        devsOnly: boolean;
    };
}) => {
    let queries: Query[] = [];
    const body: Omit<TemplateDataSettings, "dateRange"> = React.useMemo(() => {
        template.rows.forEach((row) => {
            row.items.forEach((i) => {
                if (!i.query?.id) return;
                const queryVars = {};
                if (i.query.variables && Object.keys(i.query.variables).length) {
                    Object.keys(i.query.variables).forEach((key) => {
                        if (variables && variables[key]) {
                            queryVars[key] = variables[key];
                        } else if (
                            i.query.variables &&
                            i.query.variables[key] &&
                            !i.query.variables[key].toString().startsWith("$")
                        ) {
                            queryVars[key] = i.query.variables[key];
                        }
                    });
                }
                queries.push({
                    id: i.query.id,
                    extractFromProperty: i.query.extractFromProperty,
                    assignToProperty: i.query.assignToProperty,
                    ...(Object.keys(queryVars).length && { variables: queryVars }),
                });
            });
        });

        if (executiveArgs) {
            if (executiveArgs.aggregationPeriod) {
                queries = queries.map((q) => ({
                    ...q,
                    variables: {
                        ...q.variables,
                        aggregationPeriod: executiveArgs.aggregationPeriod,
                    },
                }));
            }
            if (executiveArgs.selectedTeam === "integrated") {
                return {
                    queries,
                    users: `integrated${executiveArgs.devsOnly ? `-developers` : ""}`,
                    teamId: undefined,
                    defaultDateRange,
                };
            }
            if (executiveArgs.selectedTeam === "all") {
                return {
                    queries,
                    users: `all${executiveArgs.devsOnly ? `-developers` : ""}`,
                    teamId: undefined,
                    defaultDateRange,
                };
            }
            return {
                queries,
                users: `team${executiveArgs.devsOnly ? `-developers` : ""}`,
                teamId: executiveArgs.selectedTeam,
                defaultDateRange,
            };
        }
        if (teamId) {
            return {
                queries,
                users: "team",
                teamId,
                defaultDateRange,
            };
        }
        if (repos || groups) {
            return {
                queries,
                users: "none",
                repos,
                groups,
                defaultDateRange,
            };
        }
        return {
            queries,
            users: "me",
            defaultDateRange,
        };
    }, [
        template && JSON.stringify(template),
        repos,
        groups,
        teamId,
        variables && JSON.stringify(variables),
        executiveArgs && JSON.stringify(executiveArgs),
        isSample,
    ]);

    return useTemplateApi({
        requestMethodMock: (_body) => {
            return getTemplateMockData(_body);
        },
        url: `/api/data/v1/template`,
        body,
        isTeams: !!teamId,
        isSample: !!isSample,
    }) as FetchAction;
};

export default useTemplateDataApi;
